import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn, MenuItemSingleSelect, ToolButton } from '@crabs-ai/ds-react';
import { ArrowDropDown } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import type { EditMode } from '../edit-mode';

export interface ToolConfig {
  mode: EditMode;
  titleKey: string;
  shortcut?: string;
  Icon: React.ComponentType<{ className?: string }>;
  SelectedIcon?: React.ComponentType<{ className?: string }>;
}

interface EditToolGroupProps {
  tools: ToolConfig[];
  activeTool: ToolConfig;
  isGroupActive: boolean;
  editMode: EditMode;
  onSelectTool: (mode: EditMode) => void;
}

export function EditToolGroup({
  tools,
  activeTool,
  isGroupActive,
  editMode,
  onSelectTool,
}: EditToolGroupProps) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const hasDropdown = tools.length > 1;
  const ActiveIcon =
    isGroupActive && activeTool.SelectedIcon
      ? activeTool.SelectedIcon
      : activeTool.Icon;

  const handleOpen = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsVisible(true));
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 180);
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className="relative flex items-center"
      ref={containerRef}
    >
      <ToolButton
        size="sm"
        state={isGroupActive ? 'selected' : 'default'}
        isTransparent={false}
        onClick={() => onSelectTool(activeTool.mode)}
        isShowTooltip
        tooltipNode={t(activeTool.titleKey)}
        tooltipSubNode={
          activeTool.shortcut ? `[${activeTool.shortcut}]` : undefined
        }
        tooltipPosition="top"
      >
        <ActiveIcon
          key={activeTool.mode}
          className="duration-200 animate-in fade-in zoom-in-90"
        />
        <span
          className={cn(
            'text-sm leading-none whitespace-nowrap',
            isGroupActive
              ? 'text-sys-cont-primary-default'
              : 'text-sys-cont-neutral-default',
          )}
        >
          {t(activeTool.titleKey)}
        </span>
        {hasDropdown && (
          <span
            className="ml-0 flex items-center pl-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle();
            }}
          >
            <ArrowDropDown
              className={cn(
                'transition-transform duration-200 ease-out',
                isOpen && 'rotate-180',
              )}
            />
          </span>
        )}
      </ToolButton>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 z-50 mt-3xs flex w-max origin-top-left flex-col rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs shadow-[0_0_16px_0_rgba(4,4,5,0.08)]',
            'transition-all duration-180 ease-out',
            isVisible
              ? 'translate-y-0 scale-100 opacity-100'
              : '-translate-y-1 scale-[0.97] opacity-0',
          )}
        >
          {tools.map((tool, index) => (
            <div
              key={tool.mode}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(-4px)',
                transition: `opacity 180ms ease-out ${index * 25}ms, transform 180ms ease-out ${index * 25}ms`,
              }}
            >
              <MenuItemSingleSelect
                size="md"
                icon={<tool.Icon />}
                text={t(tool.titleKey)}
                subText={tool.shortcut ? `[${tool.shortcut}]` : undefined}
                showSubText={!!tool.shortcut}
                showLabel={false}
                isSelected={editMode === tool.mode}
                onClick={() => {
                  onSelectTool(tool.mode);
                  handleClose();
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
